import { useState } from "react";
import { useCanEdit } from "../../hooks/useCanEdit.js";
import { useGrantOnly } from "../../hooks/useGrantOnly.js";
import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";
import { trpc } from "../../lib/trpc.js";

/**
 * CEO 2026-10-27 — CRÉDITO DO CLIENTE (sobra de depósito que pagou mais do
 * que as faturas): cartão na ficha do cliente e linha no painel da fatura.
 * Abater numa fatura cria um recebimento ligado ao mesmo depósito.
 */

const money = (v: number) => formatMoney(v, { decimals: 2 });
const dataBr = (s: string | Date | null | undefined) =>
  s ? new Date(s).toLocaleDateString("fr-FR") : "—";
const AVISO = { background: "#ECFCCB", borderColor: "#65A30D", color: "#1A2E05" };

function erroLegivel(t: ReturnType<typeof useT>, msg: string): string {
  if (msg === "CREDIT_NONE_AVAILABLE") return t("customerCredit.errNone");
  if (msg === "CREDIT_INVOICE_NOT_OPEN") return t("customerCredit.errNotOpen");
  return msg;
}

/** Ficha do cliente: some quando o cliente nunca teve crédito. */
export function CustomerCreditCard({
  companyId,
  customerId,
}: {
  companyId: number;
  customerId: number;
}) {
  const t = useT();
  // CEO 2026-10-27 — abater é decisão do escritório: gerente (por menu) só vê.
  const { grantOnly } = useGrantOnly();
  const canEdit = useCanEdit() && !grantOnly;
  const utils = trpc.useUtils();
  const q = trpc.customerCredits.byCustomer.useQuery({ companyId, customerId });
  const [abrindo, setAbrindo] = useState(false);
  const [faturaId, setFaturaId] = useState<number | null>(null);
  const [valor, setValor] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const mut = trpc.customerCredits.useOnInvoice.useMutation({
    onSuccess: (r) => {
      setMsg(`${t("customerCredit.usedOk")} ${money(r.aplicadoTtc)} → ${r.invoiceNumber}`);
      setAbrindo(false);
      setFaturaId(null);
      setValor("");
      void utils.customerCredits.invalidate();
      void utils.customerInvoices.invalidate();
      void utils.customers.invalidate();
      void utils.dossiers.invalidate();
    },
    onError: (e) => setErro(erroLegivel(t, e.message)),
  });

  const d = q.data;
  if (!d || d.creditos.length === 0) return null;
  const fatura = d.faturasAbertas.find((f) => f.id === faturaId) ?? null;
  const maximo = fatura ? Math.min(d.disponivelTtc, fatura.saldoTtc) : d.disponivelTtc;

  return (
    <section
      className="rounded-lg border px-4 py-3 text-sm"
      style={AVISO}
      data-testid="customer-credit-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">
          💰 {t("customerCredit.cardTitle")}:{" "}
          <span className="font-mono tabular-nums">{money(d.disponivelTtc)} TTC</span>{" "}
          {t("customerCredit.available")}
        </p>
        {canEdit && d.disponivelTtc > 0.005 && !abrindo && (
          <button
            type="button"
            onClick={() => {
              setAbrindo(true);
              setErro(null);
              setMsg(null);
              const primeira = d.faturasAbertas[0] ?? null;
              setFaturaId(primeira?.id ?? null);
              setValor(primeira ? Math.min(d.disponivelTtc, primeira.saldoTtc).toFixed(2) : "");
            }}
            className="rounded-md border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: "#65A30D", background: "#FFFFFF", color: "#1A2E05" }}
            data-testid="customer-credit-use"
          >
            {t("customerCredit.useBtn")}
          </button>
        )}
      </div>
      <p className="mt-1 text-xs opacity-80">{t("customerCredit.cardHint")}</p>
      <ul className="mt-2 space-y-1 text-xs">
        {d.creditos.map((c) => (
          <li key={c.id}>
            {t("customerCredit.originPre")} {dataBr(c.txDate)} ·{" "}
            <span className="font-mono tabular-nums">{money(c.amountTtc)}</span>
            {c.usos.map((u, i) => (
              <span key={i}>
                {" "}
                · {t("customerCredit.usedPre")} {money(u.amountTtc)} {t("customerCredit.usedOn")}{" "}
                {u.invoiceNumber} ({dataBr(u.createdAt)})
              </span>
            ))}
          </li>
        ))}
      </ul>
      {msg && <p className="mt-2 text-xs font-semibold">✓ {msg}</p>}
      {abrindo && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          {d.faturasAbertas.length === 0 ? (
            <p className="text-xs">{t("customerCredit.noOpenInvoices")}</p>
          ) : (
            <>
              <label className="block text-xs">
                {t("customerCredit.pickInvoice")}
                <select
                  value={faturaId ?? ""}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    setFaturaId(id);
                    const f = d.faturasAbertas.find((x) => x.id === id);
                    if (f) setValor(Math.min(d.disponivelTtc, f.saldoTtc).toFixed(2));
                  }}
                  className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-sm text-neutral-900"
                  data-testid="customer-credit-invoice"
                >
                  {d.faturasAbertas.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.invoiceNumber} · {t("customerCredit.balanceWord")} {money(f.saldoTtc)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                {t("customerCredit.amountLabel")}
                <input
                  type="number"
                  step="0.01"
                  min={0.01}
                  max={maximo}
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  className="mt-1 w-32 rounded-md border border-neutral-300 bg-white px-2 py-1.5 font-mono text-sm text-neutral-900"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAbrindo(false)}
                  className="rounded-md border border-neutral-400 px-3 py-1.5 text-xs"
                >
                  {t("customerCredit.cancel")}
                </button>
                <button
                  type="button"
                  disabled={mut.isPending || faturaId == null || !(Number(valor) > 0)}
                  onClick={() => {
                    setErro(null);
                    if (faturaId == null) return;
                    mut.mutate({
                      companyId,
                      customerInvoiceId: faturaId,
                      amountTtc: Math.min(Number(valor), maximo),
                    });
                  }}
                  className="rounded-md px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                  style={{ background: "#4D7C0F" }}
                  data-testid="customer-credit-confirm"
                >
                  {mut.isPending ? "…" : t("customerCredit.confirmUse")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {erro && <p className="mt-2 text-xs font-semibold text-red-700">{erro}</p>}
    </section>
  );
}

/** Painel da fatura: crédito disponível do cliente + "Abater nesta fatura". */
export function InvoiceCreditLine({
  companyId,
  invoiceId,
}: {
  companyId: number;
  invoiceId: number;
}) {
  const t = useT();
  const { grantOnly } = useGrantOnly();
  const canEdit = useCanEdit() && !grantOnly;
  const utils = trpc.useUtils();
  const q = trpc.customerCredits.forInvoice.useQuery({ companyId, invoiceId });
  const [erro, setErro] = useState<string | null>(null);
  const mut = trpc.customerCredits.useOnInvoice.useMutation({
    onSuccess: () => {
      void utils.customerCredits.invalidate();
      void utils.customerInvoices.invalidate();
      void utils.customers.invalidate();
      void utils.dossiers.invalidate();
    },
    onError: (e) => setErro(erroLegivel(t, e.message)),
  });
  const d = q.data;
  if (!d) return null;
  return (
    <div
      className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs"
      style={AVISO}
      data-testid="invoice-credit-line"
    >
      <span>
        💰 {t("customerCredit.invoicePanelPre")}{" "}
        <strong className="font-mono tabular-nums">{money(d.disponivelTtc)} TTC</strong>
      </span>
      {canEdit && (
        <button
          type="button"
          disabled={mut.isPending}
          onClick={() => {
            setErro(null);
            mut.mutate({ companyId, customerInvoiceId: invoiceId, amountTtc: d.aplicavelTtc });
          }}
          className="rounded-md px-3 py-1 font-semibold text-white disabled:opacity-40"
          style={{ background: "#4D7C0F" }}
          data-testid="invoice-credit-apply"
        >
          {mut.isPending
            ? "…"
            : `${t("customerCredit.invoicePanelBtn")} (${money(d.aplicavelTtc)} TTC)`}
        </button>
      )}
      {erro && <span className="w-full font-semibold text-red-700">{erro}</span>}
    </div>
  );
}
